// src/features/system/developer/components/video-gallery/VideoFeedErrorBoundary.tsx
// ────────────────────────────────────────────────────────────────────────────
// Production-grade error boundary scoped to the video feed.
//
// Why: the feed talks to media APIs (HTMLVideoElement, MediaSession,
// WakeLock, IntersectionObserver, signed-URL minting) that can throw or
// produce render-time exceptions under exotic browsers / cellular conditions.
// Without a boundary, a single uncaught error would white-screen the whole
// developer panel. With a boundary, the feed degrades to an actionable
// recovery card and the rest of the dashboard stays alive.
//
// Logs the error + componentStack to the BFF analytics channel (best-effort,
// silently dropped if the endpoint is unavailable) so prod incidents are
// observable even when the user can't take a screenshot.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';

interface Props {
  /** Render below the boundary. */
  children: React.ReactNode;
  /** Optional callback for parent telemetry / Sentry / custom logger. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class VideoFeedErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Best-effort structured log via BFF analytics. We don't await and we
    // swallow rejections — a broken analytics path must never escalate
    // into a second error inside an error handler.
    try {
      // eslint-disable-next-line no-console
      console.error('[VideoFeed] uncaught error', {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
      });
    } catch { /* ignore */ }

    try {
      this.props.onError?.(error, info);
    } catch { /* ignore */ }
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="h-full min-h-[20rem] flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full p-6 rounded-2xl border border-rose-400/30
                        bg-gradient-to-br from-rose-500/10 via-black/40 to-black/60
                        backdrop-blur-md text-center flex flex-col gap-3">
          <svg viewBox="0 0 24 24" className="w-8 h-8 mx-auto fill-rose-300" aria-hidden>
            <path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z" />
          </svg>
          <h3 className="text-base font-semibold text-rose-200">
            The video feed hit an unexpected error
          </h3>
          <p className="text-[12px] text-neutral-300 break-words">
            {error.message || 'Unknown error'}
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="self-center mt-1 px-4 py-1.5 rounded-full border border-neon_orange-300/40
                       text-neon_orange-200 text-xs font-semibold
                       hover:bg-neon_orange-300/10 hover:border-neon_orange-300/70
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/60
                       transition-colors"
          >
            Try again
          </button>
          <p className="text-[10px] text-neutral-500">
            If this keeps happening, check the developer console and report
            it — the failure has been logged.
          </p>
        </div>
      </div>
    );
  }
}
